import React, { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";

import TableContainer from "../../Components/Common/TableContainer";
import Flatpickr from "react-flatpickr";
import {
  getGovtRequests,
  deleteGovtRequest,
} from "common/services/govt-requests.service";
import { getSortObj } from "common/lib/tableDataOptions";
import {
  buildListParams,
  parseFilter,
  parsePage,
  parsePageSize,
  parseSearch,
  parseSort,
} from "common/lib/listUrlParams";

import Permission from "Components/Common/Permission";

import DeleteModal from "Components/Common/DeleteModal";
import { normalize } from "common/lib/normalize";
import { toast } from "react-toastify";
import BreadCrumb from "Components/Common/BreadCrumb";
import RequestTypeFilter from "./RequestTypeFilter";
import Permissions from "Components/Common/Permissions";
import BulkActionBar from "Components/Common/BulkActionBar";
import { useProfile } from "Components/Hooks/UserHooks";
import { isAllowed } from "common/lib/permissionUtils";
import GovernmentRequestsImport from "./GovernmentRequestsImport";

interface DateRange {
  fromDate: Date | null;
  toDate: Date | null;
}

interface FilterValue {
  requestType: string;
  dateRange: DateRange;
}

const defaultSort = [{ id: "createdAt", desc: true }];

const defaultFilterValue: FilterValue = {
  requestType: "",
  dateRange: {
    fromDate: null,
    toDate: null,
  },
};

const GovernmentRequests = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [params] = useState({
    page: parsePage(searchParams),
    pageSize: parsePageSize(searchParams),
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageIndex
  const setPage = (newPage: number) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      pageIndex: newPage - 1,
    }));
  };

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => parseSort(searchParams.get("sort"), defaultSort));

  const [selectedRecord, setSelectedRecord] = useState<any>({});

  const [searchValue, setSearchValue] = useState(parseSearch(searchParams));
  
  const [filterValue, setFilterValue] = useState(() => parseFilter(searchParams, defaultFilterValue));

  const [selectedCheckBox, setSelectedCheckBox] = useState<any>([]);
  const unCheckAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll") as HTMLInputElement;
    if (checkall) checkall.checked = false;
    document
      .querySelectorAll<HTMLInputElement>(".tableCheckbox")
      .forEach((checkbox) => {
        checkbox.checked = false;
      });
    setSelectedCheckBox([]);
  }, []);

  const onSelectCheckbox = () => {
    setSelectedCheckBox(
      document.querySelectorAll<HTMLInputElement>(".tableCheckbox:checked")
    );
  };

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll") as HTMLInputElement;
    document
      .querySelectorAll<HTMLInputElement>(".tableCheckbox")
      .forEach((checkbox) => {
        checkbox.checked = checkall.checked;
      });
    onSelectCheckbox();
  }, []);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});
  const [apiParams, setApiParams] = useState<any>({});
  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      unCheckAll();
      const params = buildListParams(searchParams, {
        page,
        pageSize: pagination.pageSize,
        sorted,
        searchValue,
        filterValue,
        defaultFilterValue,
        defaultSort,
      });
      setSearchParams(params, {
        replace: true,
      });

      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.requestType) {
        andConditions.push({
          typeOfRequest: {
            id: {
              $eq: filterValue.requestType,
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        andConditions.push({
          dateOfRequest: {
            $between: [
              filterValue.dateRange.fromDate.toLocaleDateString("en-CA"),
              filterValue.dateRange.toDate.toLocaleDateString("en-CA"),
            ],
          },
        });
      }

      const search = searchValue.trim();
      if (search) {
        andConditions.push({
          $or: [
            { requestTitle: { $containsi: search } },
            { projectId: { $containsi: search } },
            { dataProvidedBy: { $containsi: search } },
            { mda: { name: { $containsi: search } } },
            { mda: { region: { name: { $containsi: search } } } },
            { agency: { name: { $containsi: search } } },
            { sector: { name: { $containsi: search } } },
          ],
        });
      }

      try {
        setLoading(true);

        const requestParams = {
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "agency",
            "mda",
            "mda.region",
            "individual",
            "positionType",
            "typeOfRequest",
          ],
          filters: {
            $and: andConditions,
          },
        };
        setApiParams({
          sort: requestParams.sort,
          filters: requestParams.filters,
        });

        const response: any = await getGovtRequests(requestParams);
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const [deleteModal, setDeleteModal] = useState(false);

  const onClickDelete = useCallback(
    (data: any) => {
      setSelectedRecord(data);
      setDeleteModal(true);
    },
    [setSelectedRecord, setDeleteModal]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted: sorted,
      searchValue: searchValue,
      filterValue: filterValue,
    });
  }, [filterValue, onPageChange, pagination.pageIndex, sorted, searchValue]);

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    if (selectedRecord) {
      setDeleting(true);
      try {
        await deleteGovtRequest(selectedRecord.id);
        fetchUpdatedData();
        setDeleting(false);
        setDeleteModal(false);
        toast.success("Request deleted successfully");
      } catch (e: any) {
        toast.error("Error while deleting request");
      } finally {
        setDeleting(false);
      }
    }
  };

  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const handleDeleteMultiple = async () => {
    const selectedIds = Array.from(selectedCheckBox).map(
      (checkbox: any) => checkbox.value
    );
    if (!selectedIds.length) return;

    setDeleting(true);
    try {
      await Promise.all(selectedIds.map((id) => deleteGovtRequest(id)));
      unCheckAll();
      fetchUpdatedData();
      setDeleteMultipleModal(false);
      toast.success("Government requests deleted successfully");
    } catch (e: any) {
      toast.error("Error while deleting government requests");
    } finally {
      setDeleting(false);
    }
  };

  const { userProfile } = useProfile();
  const columnVisibility = useMemo(
    () => ({
      checkBoxColumn: isAllowed(userProfile?.permissions, [
        { resource: "government-request", action: "delete" },
      ]),
    }),
    [userProfile?.permissions]
  );

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={checkedAll}
          />
        ),
        cell: (cell: any) => (
          <input
            type="checkbox"
            className="tableCheckbox form-check-input"
            value={cell.getValue()}
            onChange={onSelectCheckbox}
          />
        ),
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Ministry/Department/Agency (MDA)",
        accessorKey: "mda.name",
        id: "mda.name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-bank-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link
                          to={`/government-requests/${rowData.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                          {cell.row.original?.mda?.acronym
                            ? ` (${cell.row.original?.mda?.acronym})`
                            : ""}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/government-requests/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                        {cell.row.original?.mda?.acronym
                          ? ` (${cell.row.original?.mda?.acronym})`
                          : ""}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.mda?.region?.name || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },
      {
        header: "Type of Request",
        accessorKey: "typeOfRequest.name",
        enableColumnFilter: false,
        id: "typeOfRequest.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },
      {
        header: "Period",
        accessorKey: "periodInMonths",
        enableColumnFilter: false,
        id: "periodInMonths",
        cell: (cell: any) => {
          const data = cell.getValue();
          if (!data) return "Not Provided";
          const label = data === 1 ? "Month" : "Months";
          return (
            <>
              <h5 className="fs-14 my-1 fw-normal">{data}</h5>
              <span className="text-muted">{label}</span>
            </>
          );
        },
      },

      {
        header: "Reporting Agency",
        accessorKey: "agency.name",
        enableColumnFilter: false,
        id: "agency.name",
        cell: (cell: any) => {
          return <>{cell.getValue() || "Not Provided"}</>;
        },
      },

      {
        header: "Funding Status",
        accessorKey: "fundingStatus",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          return cell.getValue() || "Not Provided";
        },
      },
      {
        header: "Value of Request",
        accessorKey: "totalValueOfRequest",
        enableColumnFilter: false,
        id: "totalValueOfRequest",
        cell: (cell: any) => {
          return (
            <>
              {cell.getValue()
                ? `$ ${cell.getValue().toLocaleString()}`
                : "Not Provided"}
            </>
          );
        },
      },

      //   {
      //     header: "Start Date",
      //     accessorKey: "startDate",
      //     id: "startDate",
      //     enableColumnFilter: false,
      //     cell: (cell: any) => {
      //       if (cell.getValue() === null) return null;
      //       const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
      //       return <>{date}</>;
      //     },
      //   },

      //   {
      //     header: "End Date",
      //     accessorKey: "endDate",
      //     id: "endDate",
      //     enableColumnFilter: false,
      //     cell: (cell: any) => {
      //       if (cell.getValue() === null) return null;
      //       const date = moment(new Date(cell?.getValue())).format("DD MMM Y");
      //       return <>{date}</>;
      //     },
      //   },

      {
        header: "Action",
        enableSorting: false,
        cell: (cellProps: any) => {
          const rowData = cellProps.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm dropdown"
                tag="button"
              >
                <i className="ri-more-fill align-middle"></i>
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission resource="government-request" action="findOne">
                  <DropdownItem
                    href={`/government-requests/${rowData.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/government-requests/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>{" "}
                    View
                  </DropdownItem>
                </Permission>

                <Permission resource="government-request" action="update">
                  <DropdownItem
                    href="/#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/government-requests/update/${rowData.id}`);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>{" "}
                    Edit
                  </DropdownItem>
                </Permission>

                <Permission resource="government-request" action="delete">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={() => {
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>{" "}
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Government Requests"
            pageTitles={[{ title: "Dashboard", link: "/dashboard" }]}
          />

          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => handleDelete()}
            onCloseClick={() => setDeleteModal(false)}
            loading={deleting}
          />
          <DeleteModal
            show={deleteMultipleModal}
            onDeleteClick={handleDeleteMultiple}
            onCloseClick={() => setDeleteMultipleModal(false)}
            loading={deleting}
          />
          <Card>
            <CardBody>
              <Row className="g-3 ">
                <Col xxl={5} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">Search</h6> */}
                  <div className="search-box">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search government requests..."
                      onChange={(e) => {
                        setSearchValue(e.target.value);
                        setPage(1);
                      }}
                      value={searchValue}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>

                <Col xxl={3} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">
                    Select Date Range
                  </h6> */}
                  {/* @ts-ignore */}
                  <Flatpickr
                    className="form-control"
                    placeholder="Select Date Range"
                    options={{
                      mode: "range",
                      altFormat: "d M, Y",
                      dateFormat: "d M, Y",
                    }}
                    onChange={(e) => {
                      if (e[0] && e[1]) {
                        setFilterValue({
                          ...filterValue,
                          dateRange: {
                            fromDate: e[0],
                            toDate: e[1],
                          },
                        });
                        setPage(1);
                      }
                    }}
                    value={[
                      filterValue?.dateRange?.fromDate || "",
                      filterValue?.dateRange?.toDate || "",
                    ]}
                  />
                </Col>
                <Col xxl={3} lg={6}>
                  {/* <h6 className="text-uppercase fs-12 mb-2">
                    Select Type of Request
                  </h6> */}
                  <RequestTypeFilter
                    onChange={(value: any) => {
                      setFilterValue({ ...filterValue, requestType: value });
                      setPage(1);
                    }}
                    value={filterValue.requestType}
                  />
                </Col>
                <Col xxl={1} lg={1}>
                  {/* <h6 className="text-uppercase fs-12 mb-xxl-4"> {` `}</h6> */}
                  <div className="w-100 d-flex gap-2">
                    <button
                      className="flex-grow-1 btn btn-light"
                      onClick={() => {
                        setFilterValue(defaultFilterValue);
                        setSearchValue("");
                        setPage(1);
                        setSorted([{ id: "createdAt", desc: true }]);
                      }}
                    >
                      <i className="ri-restart-line align-bottom me-1"></i>
                      Reset{" "}
                    </button>

                    {/* <Button
                               color="primary"
                               className="flex-grow-1"
                               onClick={() => {
                                 setAdvancedFilter(true);
                               }}
                             >
                               <i className="ri-equalizer-line align-bottom me-1"></i>
                               Filters
                             </Button> */}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card>
            <CardHeader>
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <h5 className="card-title mb-1">Government Requests</h5>
                  <p className="mb-0 text-muted">
                    A list of government requests made to UN Agencies from
                    ministries, departments and agencies.
                  </p>
                </Col>
                <div className="col-md-auto ms-auto">
                  <div className="d-flex gap-2">
                    <Permission resource="government-request" action="create">
                      <Link
                        to="#"
                        className="btn btn-primary"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate("/government-requests/add");
                        }}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Add Request
                      </Link>
                    </Permission>
                    <GovernmentRequestsImport
                      apiParams={apiParams}
                      onImported={fetchUpdatedData}
                    />
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody>
              <TableContainer
                loading={loading}
                columns={columns}
                data={data || []}
                pagination={pagination}
                setPagination={setPagination}
                sorted={sorted}
                setSorted={setSorted}
                onPageChange={onPageChange}
                FilterSection={() => {}}
                totalRows={meta?.pagination?.total || 0}
                totalPages={meta?.pagination?.pageCount || 0}
                searchValue={searchValue}
                filterValue={filterValue}
                columnVisibility={columnVisibility}
                divClass="table-responsive table-card mb-4"
                tableClass="align-middle table-nowrap table-striped mb-0"
                theadClass="table-light table-nowrap"
              />
            </CardBody>
          </Card>
        </Container>
        <Permissions
          checks={[{ resource: "government-request", action: "delete" }]}
        >
          <BulkActionBar
            count={selectedCheckBox.length}
            onClear={unCheckAll}
            itemLabel="government request"
          >
            <Button
              color="danger"
              size="sm"
              className="material-shadow-none"
              onClick={() => setDeleteMultipleModal(true)}
            >
              <i className="ri-delete-bin-line align-bottom me-1"></i> Delete
            </Button>
          </BulkActionBar>
        </Permissions>
      </div>
    </React.Fragment>
  );
};

export default GovernmentRequests;
